import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useLeague } from "../hooks/useLeague";
import { useRequest } from "../hooks/useRequest";
import ModalDialog from "../Common/ModalDialog";
import {
  getGamesUrl,
  getGameUrl,
  getTeamsUrl,
  getSeasonUrl,
} from "../config/api";
import { apiPost, apiPatch, apiDelete } from "../utils/apiClient";
import QuickScoreModal from "./QuickScoreModal";
import { Game, Team, Season } from "../types/models";
import { PaginatedResponse } from "../types/api";
import { adminGameUrl } from "./adminGameLinks";
import TeamDisplay from "../Common/TeamDisplay";
import { formatGameDateTime, formatGameDateForInput } from "../utils/dateUtils";
import {
  suggestNewGame,
  EMPTY_SUGGESTION,
} from "../utils/gameScheduleSuggestions";

interface GameFormElement extends HTMLFormElement {
  homeTeamField: HTMLSelectElement;
  visitorTeamField: HTMLSelectElement;
  dateField: HTMLInputElement;
  gameTypeField: HTMLSelectElement;
}

type SortField = "date" | "home_team_name" | "visitor_team_name";
type SortDirection = "asc" | "desc";

// Helper Components
function TableLoadingView() {
  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md="auto">
          <Spinner animation="grow" /> Loading...
        </Col>
      </Row>
    </Container>
  );
}

function sortGames(
  games: Game[],
  field: SortField,
  direction: SortDirection,
): Game[] {
  return [...games].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    if (field === "date") {
      aVal = new Date(a.date).getTime();
      bVal = new Date(b.date).getTime();
    } else {
      aVal = (a[field] || "").toLowerCase();
      bVal = (b[field] || "").toLowerCase();
    }

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
}

/**
 * How many of the season's most recent games to read when inferring defaults
 * for a new game. Matches the API's maximum page size.
 */
const SUGGESTION_SAMPLE_SIZE = 200;

/**
 * Muted explanation of a prefilled value. Hidden once the admin edits the
 * field, since the reason no longer describes what is in it.
 */
function SuggestionHint({
  reason,
  hidden,
}: {
  reason: string | null;
  hidden: boolean;
}) {
  if (!reason || hidden) return null;
  return <Form.Text className="text-muted">Suggested — {reason}</Form.Text>;
}

// GamesList Component
function GamesList({
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onQuickScoreClick,
  onScoreboardClick,
  seasonId,
  refreshKey,
}: {
  onCreateClick: () => void;
  onEditClick: (game: Game) => void;
  onDeleteClick: (game: Game) => void;
  onQuickScoreClick: (game: Game) => void;
  onScoreboardClick: (game: Game) => void;
  seasonId: number;
  refreshKey: number;
}) {
  const auth = useAuth();
  const { canManageLeague, canScoreGames } = useLeagueMembership();
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const skip = (currentPage - 1) * pageSize;
  const { data: gamesResponse, error } = useRequest<PaginatedResponse<Game>>(
    `${getGamesUrl(
      seasonId,
    )}?limit=${pageSize}&skip=${skip}&_refresh=${refreshKey}`,
    auth.token,
  );

  const { data: teamsResponse } = useRequest<PaginatedResponse<Team>>(
    getTeamsUrl(seasonId),
    auth.token,
  );

  // Reset to page 1 when refreshKey changes (after create/edit/delete)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [refreshKey]);

  const games = gamesResponse?.data;
  const teams = teamsResponse?.data;
  const total = gamesResponse?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Build team map for name lookups
  const teamMap = new Map(teams?.map((t) => [t.id, t]));

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-muted">⇅</span>;
    return <span>{sortDirection === "asc" ? "▲" : "▼"}</span>;
  };

  if (error) {
    return <Alert variant="danger">Error loading games: {error.message}</Alert>;
  }

  if (!games) {
    return <TableLoadingView />;
  }

  const sortedGames = sortGames(games, sortField, sortDirection);

  return (
    <>
      {canManageLeague && (
        <div className="d-flex justify-content-end mb-2">
          <Button onClick={onCreateClick}>New</Button>
        </div>
      )}

      {games.length === 0 &&
        (canManageLeague ? (
          <p>Click "New" to create your first game.</p>
        ) : (
          <p>This season has no games scheduled yet.</p>
        ))}

      {games.length > 0 && (
        <Table striped bordered hover className="crud">
          <thead>
            <tr>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("date")}
              >
                Date <SortIndicator field="date" />
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("visitor_team_name")}
              >
                Away Team <SortIndicator field="visitor_team_name" />
              </th>
              <th>Away Score</th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("home_team_name")}
              >
                Home Team <SortIndicator field="home_team_name" />
              </th>
              <th>Home Score</th>
              <th>Status</th>
              {canScoreGames && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedGames.map((game) => {
              const homeTeam = teamMap.get(game.home_team_id);
              const visitorTeam = teamMap.get(game.visitor_team_id);
              const homeTeamName =
                game.home_team_name || homeTeam?.name || "TBD";
              const visitorTeamName =
                game.visitor_team_name || visitorTeam?.name || "TBD";

              // Create enhanced game object with computed team names
              const gameWithNames = {
                ...game,
                home_team_name: homeTeamName,
                visitor_team_name: visitorTeamName,
              };

              return (
                // The row itself opens the scoreboard, which is what an admin
                // picks a game out of this list to do. The action buttons stop
                // the click from reaching it.
                <tr
                  key={game.id}
                  className={canScoreGames ? "admin-row-clickable" : undefined}
                  role={canScoreGames ? "button" : undefined}
                  tabIndex={canScoreGames ? 0 : undefined}
                  onClick={
                    canScoreGames
                      ? () => onScoreboardClick(gameWithNames)
                      : undefined
                  }
                  onKeyDown={
                    canScoreGames
                      ? (event) => {
                          if (event.key !== "Enter" && event.key !== " ")
                            return;
                          event.preventDefault();
                          onScoreboardClick(gameWithNames);
                        }
                      : undefined
                  }
                >
                  <td>{formatGameDateTime(game.date)}</td>
                  <td>
                    <TeamDisplay
                      team={{
                        id: game.visitor_team_id,
                        name: visitorTeamName,
                        colour_code:
                          game.visitor_team_colour_code ||
                          visitorTeam?.colour_code,
                      }}
                    />
                  </td>
                  <td>{game.visitor_score ?? "-"}</td>
                  <td>
                    <TeamDisplay
                      team={{
                        id: game.home_team_id,
                        name: homeTeamName,
                        colour_code:
                          game.home_team_colour_code || homeTeam?.colour_code,
                      }}
                    />
                  </td>
                  <td>{game.home_score ?? "-"}</td>
                  <td>
                    <Badge
                      bg={
                        game.state === "completed"
                          ? "success"
                          : game.state === "playing"
                            ? "danger"
                            : "secondary"
                      }
                    >
                      {game.state === "completed"
                        ? "Completed"
                        : game.state === "playing"
                          ? "Live"
                          : "Scheduled"}
                    </Badge>
                  </td>
                  {canScoreGames && (
                    <td
                      align="right"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onQuickScoreClick(gameWithNames)}
                      >
                        Quick Score
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => onScoreboardClick(gameWithNames)}
                      >
                        Scoreboard
                      </Button>
                      {canManageLeague && (
                        <>
                          {" "}
                          <Button size="sm" onClick={() => onEditClick(game)}>
                            Edit
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => onDeleteClick(gameWithNames)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            />

            {/* Show page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}

            <Pagination.Next
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </>
  );
}

// GamesFormView Component
function GamesFormView({
  game,
  onCancelClick,
  seasonId,
}: {
  game?: Game;
  onCancelClick: () => void;
  seasonId: number;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [editedFields, setEditedFields] = useState<Record<string, boolean>>({});

  const isEdit = !!game;

  const { data: teamsResponse, error: teamsError } = useRequest<
    PaginatedResponse<Team>
  >(getTeamsUrl(seasonId), auth.token);

  // Only new games get smart defaults; an existing game already has its values.
  // Newest first so a long season's recent pattern is always in the sample.
  const { data: recentGamesResponse, error: recentGamesError } = useRequest<
    PaginatedResponse<Game>
  >(
    isEdit
      ? undefined
      : `${getGamesUrl(seasonId)}?sort=-date&limit=${SUGGESTION_SAMPLE_SIZE}`,
    auth.token,
  );

  const teams = teamsResponse?.data;
  const recentGames = recentGamesResponse?.data;

  const suggestion = React.useMemo(
    () =>
      teams && recentGames
        ? suggestNewGame(recentGames, teams)
        : EMPTY_SUGGESTION,
    [teams, recentGames],
  );

  const markEdited = (field: string) =>
    setEditedFields((previous) =>
      previous[field] ? previous : { ...previous, [field]: true },
    );

  const handleSubmit: React.FormEventHandler<GameFormElement> = (event) => {
    event.preventDefault();
    setErrorMessage("");

    const homeTeamId = parseInt(event.currentTarget.homeTeamField.value);
    const visitorTeamId = parseInt(event.currentTarget.visitorTeamField.value);
    const dateTime = event.currentTarget.dateField.value;
    const gameType = event.currentTarget.gameTypeField.value as
      | "regular"
      | "exhibition"
      | "playoff";

    // Validation
    if (!homeTeamId || !visitorTeamId) {
      setErrorMessage("Both home and away teams are required");
      return;
    }

    if (homeTeamId === visitorTeamId) {
      setErrorMessage("Home and away teams must be different");
      return;
    }

    if (!dateTime) {
      setErrorMessage("Date and time are required");
      return;
    }

    setIsSubmitting(true);

    const isEdit = !!game?.id;
    const url = isEdit ? getGameUrl(game.id) : getGamesUrl(seasonId);
    const apiFn = isEdit ? apiPatch : apiPost;

    const requestBody = {
      game: {
        home_team_id: homeTeamId,
        visitor_team_id: visitorTeamId,
        date: dateTime,
        game_type: gameType,
      },
    };

    apiFn(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        onCancelClick();
      })
      .catch((error) => {
        setErrorMessage(error.message || "An error occurred");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (teamsError) {
    return (
      <Alert variant="danger">Error loading teams: {teamsError.message}</Alert>
    );
  }

  // Suggestions feed uncontrolled defaultValues, so the fields must not render
  // until the sample has resolved. A failed sample just means no suggestions.
  if (!teams || (!isEdit && !recentGames && !recentGamesError)) {
    return <TableLoadingView />;
  }

  if (teams.length === 0) {
    return (
      <Alert variant="warning">
        No teams found. Please{" "}
        <Link to={`/admin/seasons/${seasonId}/teams`}>create teams</Link> before
        scheduling games.
      </Alert>
    );
  }

  if (teams.length < 2) {
    return (
      <Alert variant="warning">
        You need at least 2 teams to create a game. Please{" "}
        <Link to={`/admin/seasons/${seasonId}/teams`}>add more teams</Link>.
      </Alert>
    );
  }

  return (
    <Container>
      <Row>
        <Col md={8}>
          <h5 className="mb-3">{game ? "Edit Game" : "New Game"}</h5>

          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="homeTeamField">
              <Form.Label>Home Team</Form.Label>
              <Form.Select
                defaultValue={game?.home_team_id ?? suggestion.homeTeamId ?? ""}
                onChange={() => markEdited("homeTeam")}
                required
                disabled={isSubmitting}
              >
                <option value="">Select Home Team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Form.Select>
              <SuggestionHint
                reason={suggestion.hostingReason}
                hidden={editedFields.homeTeam}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="visitorTeamField">
              <Form.Label>Away Team</Form.Label>
              <Form.Select
                defaultValue={
                  game?.visitor_team_id ?? suggestion.visitorTeamId ?? ""
                }
                onChange={() => markEdited("visitorTeam")}
                required
                disabled={isSubmitting}
              >
                <option value="">Select Away Team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Form.Select>
              <SuggestionHint
                reason={suggestion.pairingReason}
                hidden={editedFields.visitorTeam}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="dateField">
              <Form.Label>Date & Time</Form.Label>
              <Form.Control
                type="datetime-local"
                defaultValue={
                  game?.date
                    ? formatGameDateForInput(game.date)
                    : suggestion.date ?? ""
                }
                onChange={() => markEdited("date")}
                required
                disabled={isSubmitting}
              />
              <SuggestionHint
                reason={suggestion.dateReason}
                hidden={editedFields.date}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="gameTypeField">
              <Form.Label>Game Type</Form.Label>
              <Form.Select
                defaultValue={
                  game?.game_type ?? suggestion.gameType ?? "regular"
                }
                onChange={() => markEdited("gameType")}
                required
                disabled={isSubmitting}
              >
                <option value="regular">Regular Season</option>
                <option value="exhibition">Exhibition</option>
                <option value="playoff">Playoffs</option>
              </Form.Select>
              {/* "Regular Season" is already the standing default, so only
                  explain the game type when the schedule points somewhere else. */}
              <SuggestionHint
                reason={
                  suggestion.gameType && suggestion.gameType !== "regular"
                    ? suggestion.gameTypeReason
                    : null
                }
                hidden={editedFields.gameType}
              />
            </Form.Group>
            <Button
              onClick={onCancelClick}
              variant="secondary"
              disabled={isSubmitting}
            >
              Cancel
            </Button>{" "}
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" /> Saving...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

// Main Component
function AdminSeasonSchedulesPage() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [content, setContent] = useState<React.ReactNode>(null);
  const [showQuickScoreModal, setShowQuickScoreModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const seasonIdNum = parseInt(seasonId || "0");

  const { data: season } = useRequest<Season>(
    seasonId ? getSeasonUrl(seasonIdNum) : undefined,
    auth.token,
  );

  const seasonName = season?.name || `Season ${seasonId}`;

  // Navigation functions
  const showList = () => {
    setRefreshKey(Date.now());
    setContent(
      <GamesList
        onCreateClick={() => showForm()}
        onEditClick={(game) => showForm(game)}
        onDeleteClick={confirmDelete}
        onQuickScoreClick={handleQuickScoreClick}
        onScoreboardClick={handleScoreboardClick}
        seasonId={seasonIdNum}
        refreshKey={refreshKey}
      />,
    );
  };

  const showForm = (game?: Game) => {
    setContent(
      <GamesFormView
        game={game}
        onCancelClick={showList}
        seasonId={seasonIdNum}
      />,
    );
  };

  const confirmDelete = (game: Game) => {
    const homeTeamName = game.home_team_name || `Team ${game.home_team_id}`;
    const visitorTeamName =
      game.visitor_team_name || `Team ${game.visitor_team_id}`;

    setContent(
      <div
        className="modal show"
        style={{ display: "block", position: "initial" }}
      >
        <ModalDialog
          titleText="Delete Game"
          bodyText={`Are you sure you want to delete this game? (${homeTeamName} vs ${visitorTeamName})`}
          cancelText="Cancel"
          okText="Delete"
          onCancelClick={showList}
          onSaveClick={() => handleDelete(game)}
        />
      </div>,
    );
  };

  const handleDelete = (game: Game) => {
    apiDelete(getGameUrl(game.id), {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        showList();
      })
      .catch((error) => {
        alert(error.message || "An error occurred while deleting the game");
        showList();
      });
  };

  const handleQuickScoreClick = (game: Game) => {
    setSelectedGame(game);
    setShowQuickScoreModal(true);
  };

  const handleScoreboardClick = (game: Game) => {
    navigate(adminGameUrl(seasonIdNum, game.id));
  };

  const handleQuickScoreSave = (
    gameId: number,
    homeScore: number,
    visitorScore: number,
    state: "scheduled" | "playing" | "completed",
  ) => {
    const url = getGameUrl(gameId);
    const requestBody = {
      game: {
        home_score: homeScore,
        visitor_score: visitorScore,
        state: state,
      },
    };

    apiPatch(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        setShowQuickScoreModal(false);
        setSelectedGame(null);
        showList();
      })
      .catch((error) => {
        alert(error.message || "An error occurred while saving scores");
      });
  };

  // Initialize with list view
  if (!content) {
    showList();
  }

  return (
    <div className="mt-4">
      <h3 className="mb-4">{seasonName} Schedule</h3>
      {content}

      {showQuickScoreModal && selectedGame && (
        <QuickScoreModal
          show={showQuickScoreModal}
          game={selectedGame}
          onHide={() => {
            setShowQuickScoreModal(false);
            setSelectedGame(null);
            showList();
          }}
          onSave={handleQuickScoreSave}
        />
      )}
    </div>
  );
}

export default AdminSeasonSchedulesPage;
